import { useCallback } from 'react'

import { Item, ItemType, ShortcutItem } from '@inno-sdks/files'
import { AppId } from '@inno-tools/container-bridge'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useWindowManager } from 'modules/desktop/pages/MainPage/components/WindowManager/hooks/useWindowManager'
import { useKeyboard } from 'shared/hooks/useKeyboard'

import { useItemModal } from '../components/FilePreviewModal/hooks/useItemModal'
import {
  getItemLink,
  getShortcutInfo,
  isClusterOfficeFile,
  isDrawingFile
} from '../utils'

type UseOpenItemProps = {
  prefix?: string

  /**
   * @default false
   */
  inNewTab?: boolean
}

export const useOpenItem = ({
  inNewTab = false,
  prefix
}: UseOpenItemProps = {}) => {
  const { openAppWindow } = useWindowManager()
  const { apps } = useAvailableApps()
  const { open: openItem } = useItemModal()

  const { ctrlKey: isCtrlPressed } = useKeyboard()

  const handleOpenInNewTab = useCallback((link: string) => {
    window.open(link, '_blank')?.focus()
  }, [])

  return useCallback(
    (item: Item) => {
      const shouldOpenInNewTab = isCtrlPressed

      const itemLink = new URL(getItemLink(item, apps))
      const itemPath = itemLink.pathname + itemLink.search + itemLink.hash

      if (item.type === ItemType.Shortcut) {
        const { shortcutUrl } = (item as ShortcutItem).metaData
        const { isInternal, appId, fullPath } = getShortcutInfo(
          shortcutUrl,
          apps
        )

        // internal shortcut + app exists = open in window
        if (
          isInternal &&
          appId &&
          fullPath &&
          Object.keys(apps).includes(appId)
        ) {
          openAppWindow(appId, fullPath)
          return
        }

        handleOpenInNewTab(itemLink.toString())
        return
      }

      if (item.type === ItemType.File) {
        if (shouldOpenInNewTab) {
          return handleOpenInNewTab(itemLink.toString())
        } else if (isClusterOfficeFile(item) || isDrawingFile(item)) {
          return openAppWindow(AppId.CLUSTER_FILES, itemPath)
        }

        return openItem(item.id)
      }

      if (item.type === ItemType.Folder) {
        return openAppWindow(AppId.CLUSTER_FILES, itemPath)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inNewTab, prefix, apps]
  )
}
